import { useEffect, useState } from "react";
import ButtonComp from "@components/UI/Button/Index";
import SelectOptionModalComp from "@components/UI/SelectOptionModal/Index";
import SweetAlert from "@components/Feedback/SweetAlert/Index";
import { tipoComplianceOptions } from "@enums/Compliance/Index.jsx";
import { useGenericReducer } from "@hooks/state/useGenericReducer";
import { blockedOptionMessage } from "@utils/Index";
import { useFetchDataCascade } from "@hooks/api/useFetchDataCascade";
import { Modal } from "antd";
import GridComplianceAssociadoSolicitacao from "./Grid";

const ModalCompliance = ({ visible, closeModal, compliance, setCompliance, setValue }) => {
  const {
    yearOptions, serviceOptions, typeOptions, processOptions,
    yearLoading, serviceLoading, typeLoading, processLoading,
    fetchYears, fetchServicesByYear, fetchTypesByServiceId, fetchProcessosByType,
    setServiceOptions, setTypeOptions, setProcessOptions,
  } = useFetchDataCascade();

  const { state, setData } = useGenericReducer();

  const initialFilters = {
    codigo: null, 
    tipoCompliance: null, 
    ano: null,
    servico: null, 
    tipo: null, 
    nomeProcesso: null,
  };

  const [selectedItem, setSelectedItem] = useState(null);
  const [tempValues, setTempValues] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => { fetchYears(); setSelectedItem(null); }, []);

  const handleTempChange = (field, value) => {
    setTempValues(prev => {
      let updated = { ...prev, [field]: value };
      // Limpa dependentes na cascata
      if (field === "ano") {
        setServiceOptions([]); setTypeOptions([]); setProcessOptions([]);
        updated = { ...updated, servico: null, tipo: null, nomeProcesso: null };
        value && fetchServicesByYear(value.value);
      }
      if (field === "servico") {
        setTypeOptions([]); setProcessOptions([]);
        updated = { ...updated, tipo: null, nomeProcesso: null };
        value && fetchTypesByServiceId(value.value);
      }
      if (field === "tipo") {
        setProcessOptions([]);
        updated = { ...updated, nomeProcesso: null };
        value && fetchProcessosByType(value.value);
      }
      return updated;
    });
  };

  const handleCloseWithReset = () => {
    setTempValues(initialFilters);
    setFilters(initialFilters);
    setSelectedItem(null);
    closeModal();
  };

  const handleClearFilter = () => {
    setTempValues(initialFilters);
    setFilters(initialFilters);
    setSelectedItem(null);
  };

  const handleSearch = () => {
    setFilters({
      codigo: tempValues.codigo?.value ?? null,
      tipoCompliance: tempValues.tipoCompliance?.value ?? null,
      ano: tempValues.ano?.value ?? null,
      servico: tempValues.servico?.label ?? null,
      tipo: tempValues.tipo?.label ?? null,
      nomeProcesso: tempValues.nomeProcesso?.label ?? null,
    });
  };

  const handleConfirmSelection = () => {
    if (!selectedItem) {
      SweetAlert.error("Erro", "Nenhum compliance foi selecionado para associar.");
      return;
    }
    // Atualiza o compliance global
    setCompliance(prev => ({
      ...prev,
      selectedRow: selectedItem,
      codigoCompliance: selectedItem.codigo,
      idProcesso: selectedItem.idProcesso,
      nomeProcesso: selectedItem.nomeProcesso,
      etapaProcesso: "NAO_APLICADO",
    }));
    setValue("codigoCompliance", selectedItem.codigo);
    closeModal();
    handleCloseWithReset(); // apaga a referencia para o grid do modal
    SweetAlert.toastSuccess("Compliance Associado!");
  };

  return (
    <Modal
      getContainer={false}
      open={visible}
      onCancel={handleCloseWithReset}
      centered
      footer={null}
      width={1000}
      title={<h5 className="fw-bold">Pesquisar Compliance</h5>}
    >
      <hr />
      <div className="row">
        <div className="col-md-6">
          <SelectOptionModalComp
            label={"Compliance"}
            options={tipoComplianceOptions}
            value={tempValues.tipoCompliance}
            onChange={selected => handleTempChange('tipoCompliance', selected)}
            placeholder="Selecionar um compliance"
            hoverMessage="Selecionar um compliance"
          />
          <SelectOptionModalComp
            label={"Ano"}
            options={yearOptions}
            value={tempValues.ano}
            onChange={selected => handleTempChange('ano', selected)}
            placeholder="Selecionar um ano"
            hoverMessage="Selecionar um ano"
            isLoading={yearLoading}
          />
          <SelectOptionModalComp
            label="Serviço"
            options={serviceOptions}
            value={tempValues.servico}
            onChange={selected => handleTempChange('servico', selected)}
            placeholder={tempValues.ano ? "Selecionar um serviço" : blockedOptionMessage}
            hoverMessage="Selecionar um serviço"
            isLoading={serviceLoading}
            isDisabled={!tempValues.ano}
          />
        </div>
        <div className="col-md-6">
          <SelectOptionModalComp
            label="Tipo"
            options={typeOptions}
            value={tempValues.tipo}
            onChange={selected => handleTempChange('tipo', selected)}
            placeholder={tempValues.tipo ? "Selecionar um tipo" : blockedOptionMessage}
            hoverMessage="Selecionar um tipo"
            isLoading={typeLoading}
            isDisabled={!tempValues.servico}
          />
          <SelectOptionModalComp
            label="Processo"
            options={processOptions}
            value={tempValues.nomeProcesso}
            onChange={selected => handleTempChange('nomeProcesso', selected)}
            placeholder={tempValues.tipo ? "Selecionar um processo" : blockedOptionMessage}
            hoverMessage="Selecionar um processo"
            isLoading={processLoading}
            isDisabled={!tempValues.tipo}
          />
        </div>
        <div className="col-12 mt-4 d-flex justify-content-end">
          <ButtonComp
            title="BUSCAR"
            buttonColor="#2b97be"
            classnameIcon="fa-solid fa-magnifying-glass"
            onClick={handleSearch}
            hoverMessage={"Botão para buscar um compliance"}
          />
          <div className="mx-2">
            <ButtonComp
              title="LIMPAR FILTRO"
              buttonColor={"#125578"}
              classnameIcon={"fa-regular fa-circle-xmark"}
              hoverMessage={"Botão para limpar os campos de filtros preenchidos"}
              onClick={handleClearFilter}
              type={"button"}
            />
          </div>
          <ButtonComp
            title="CANCELAR"
            buttonColor="#dc3545"
            classnameIcon="fa-regular fa-circle-xmark"
            onClick={handleCloseWithReset}
            hoverMessage={"Botão para cancelar a atualização"}
          />
        </div>
        <GridComplianceAssociadoSolicitacao
          state={state}
          setData={setData}
          setSelectedRow={setSelectedItem}
          selectedRow={selectedItem}
          filters={filters}
        />
      </div>
      <div className="d-flex justify-content-end">
        <ButtonComp
          title="CONFIRMAR SELEÇÃO"
          buttonColor="#2b97be"
          classnameIcon="fa-solid fa-check"
          onClick={handleConfirmSelection}
          hoverMessage={"Botão para confirmar a seleção"}
        />
      </div>
    </Modal>
  );
};

export default ModalCompliance;
